/**
 * Centralized file-type icon map.
 *
 * Single source of truth for the icon shown for every uploadable file format.
 * Consumed by:
 *   - iconService.resolveEntityIcon (for uploaded File.* entities)
 *   - iconService LocalFile handling (for External.LocalFile entities)
 *   - Any other surface that needs to render a file-format icon
 *
 * Design principles:
 *   - Prefer Phosphor duotone for visual consistency with the rest of the River UI.
 *   - Use iconify simple-icons only for brand-identified formats where native
 *     brand recognition outweighs visual uniformity (Figma, Photoshop, Docker, etc.)
 *   - Every File.* entity type declared in backend/models/file_types.py
 *     MUST have an entry here; missing entries fall back to the category icon
 *     (File.Document, File.Code, ...) and ultimately to the generic File icon.
 *   - Colors follow the River palette where possible; brand colors are used
 *     only for iconify simple-icons which have established brand identity.
 */
import type { AppIcon } from '../types/apps';

export type FileIconEntry = AppIcon;

const PHOSPHOR = (phosphorIcon: string, color?: string, phosphorWeight: 'duotone' | 'fill' | 'regular' = 'duotone'): AppIcon => ({
  type: 'phosphor',
  phosphorIcon,
  phosphorWeight,
  ...(color ? { color } : {}),
});

const ICONIFY = (iconifyIcon: string, color?: string): AppIcon => ({
  type: 'iconify',
  iconifyIcon,
  ...(color ? { color } : {}),
});

export const FILE_TYPE_ICONS: Record<string, FileIconEntry> = {
  // ============ Documents ============
  'File.Document':              PHOSPHOR('FileText',    '#64748b'),
  'File.Document.PDF':          PHOSPHOR('FilePdf',     '#dc2626'),
  'File.Document.Word':         PHOSPHOR('FileDoc',     '#2b579a'),
  'File.Document.OpenDocument': PHOSPHOR('FileDoc',     '#0369a1'),
  'File.Document.RichText':     PHOSPHOR('FileText',    '#6366f1'),
  'File.Document.Pages':        PHOSPHOR('FileDoc',     '#f97316'),
  'File.Document.Text':         PHOSPHOR('FileTxt',     '#64748b'),
  'File.Document.Log':          PHOSPHOR('FileText',    '#6b7280'),
  'File.Document.Markdown':     PHOSPHOR('FileMd',      '#0f172a'),
  'File.Document.RST':          PHOSPHOR('FileText',    '#4f46e5'),
  'File.Document.LaTeX':        ICONIFY('simple-icons:latex', '#008080'),
  'File.Document.BibTeX':       ICONIFY('simple-icons:latex', '#008080'),

  // ============ Images ============
  'File.Image':       PHOSPHOR('Image',      '#0ea5e9', 'fill'),
  'File.Image.JPEG':  PHOSPHOR('FileJpg',    '#0ea5e9'),
  'File.Image.PNG':   PHOSPHOR('FilePng',    '#0ea5e9'),
  'File.Image.GIF':   PHOSPHOR('ImageSquare', '#ec4899', 'fill'),
  'File.Image.SVG':   PHOSPHOR('FileSvg',    '#a855f7'),
  'File.Image.WebP':  PHOSPHOR('Image',      '#06b6d4', 'fill'),
  'File.Image.AVIF':  PHOSPHOR('Image',      '#06b6d4', 'fill'),
  'File.Image.HEIC':  PHOSPHOR('Image',      '#0ea5e9', 'fill'),
  'File.Image.BMP':   PHOSPHOR('Image',      '#0ea5e9', 'fill'),
  'File.Image.TIFF':  PHOSPHOR('Image',      '#0ea5e9', 'fill'),
  'File.Image.Icon':  PHOSPHOR('AppWindow',  '#6366f1'),
  'File.Image.RAW':   PHOSPHOR('Camera',     '#334155'),

  // ============ Data ============
  'File.Data':              PHOSPHOR('Database',  '#7c3aed'),
  'File.Data.CSV':          PHOSPHOR('FileCsv',   '#059669'),
  'File.Data.TSV':          PHOSPHOR('FileCsv',   '#0d9488'),
  'File.Data.JSON':         PHOSPHOR('FileCode',  '#f59e0b'),
  'File.Data.JSONL':        PHOSPHOR('FileCode',  '#f59e0b'),
  'File.Data.XML':          PHOSPHOR('FileCode',  '#e34f26'),
  'File.Data.YAML':         PHOSPHOR('FileCode',  '#cb171e'),
  'File.Data.SQL':          PHOSPHOR('FileSql',   '#7c3aed'),
  'File.Data.Database':     ICONIFY('mdi:database',        '#7c3aed'),
  'File.Data.SQLite':       ICONIFY('simple-icons:sqlite', '#003b57'),
  'File.Data.Parquet':      ICONIFY('simple-icons:apacheparquet', '#50abf1'),
  'File.Data.Avro':         ICONIFY('simple-icons:apachekafka', '#231f20'),
  'File.Data.GeoJSON':      ICONIFY('mdi:earth',           '#10b981'),
  'File.Data.KML':          ICONIFY('mdi:map-marker',      '#10b981'),
  'File.Data.Shapefile':    ICONIFY('mdi:map',             '#10b981'),
  'File.Data.GPX':          ICONIFY('mdi:map-marker-path', '#10b981'),

  // ============ Spreadsheets ============
  'File.Spreadsheet':              PHOSPHOR('FileXls',             '#217346'),
  'File.Spreadsheet.Excel':        PHOSPHOR('FileXls',             '#217346'),
  'File.Spreadsheet.OpenDocument': PHOSPHOR('FileXls',             '#0369a1'),
  'File.Spreadsheet.Numbers':      ICONIFY('simple-icons:numbers', '#00a14b'),

  // ============ Code ============
  'File.Code':             PHOSPHOR('FileCode',  '#6366f1'),
  'File.Code.JavaScript':  PHOSPHOR('FileJs',    '#f7df1e'),
  'File.Code.TypeScript':  PHOSPHOR('FileTs',    '#3178c6'),
  'File.Code.Python':      PHOSPHOR('FilePy',    '#3776ab'),
  'File.Code.Jupyter':     ICONIFY('simple-icons:jupyter',    '#f37626'),
  'File.Code.Java':        ICONIFY('simple-icons:openjdk',    '#ed8b00'),
  'File.Code.Kotlin':      ICONIFY('simple-icons:kotlin',     '#7f52ff'),
  'File.Code.Scala':       ICONIFY('simple-icons:scala',      '#dc322f'),
  'File.Code.Groovy':      ICONIFY('simple-icons:apachegroovy', '#4298b8'),
  'File.Code.C':           PHOSPHOR('FileC',     '#a8b9cc'),
  'File.Code.CPlusPlus':   PHOSPHOR('FileCpp',   '#00599c'),
  'File.Code.Header':      PHOSPHOR('FileCode',  '#a8b9cc'),
  'File.Code.CSharp':      PHOSPHOR('FileCSharp', '#512bd4'),
  'File.Code.FSharp':      ICONIFY('simple-icons:fsharp',     '#378bba'),
  'File.Code.VisualBasic': ICONIFY('simple-icons:dotnet',     '#512bd4'),
  'File.Code.PHP':         ICONIFY('simple-icons:php',        '#777bb4'),
  'File.Code.Ruby':        ICONIFY('simple-icons:ruby',       '#cc342d'),
  'File.Code.Go':          ICONIFY('simple-icons:go',         '#00add8'),
  'File.Code.Rust':        PHOSPHOR('FileRs',    '#ce422b'),
  'File.Code.Swift':       ICONIFY('simple-icons:swift',      '#f05138'),
  'File.Code.R':           ICONIFY('simple-icons:r',          '#276dc3'),
  'File.Code.Dart':        ICONIFY('simple-icons:dart',       '#0175c2'),
  'File.Code.Lua':         ICONIFY('simple-icons:lua',        '#2c2d72'),
  'File.Code.Perl':        ICONIFY('simple-icons:perl',       '#39457e'),
  'File.Code.Elm':         ICONIFY('simple-icons:elm',        '#1293d8'),
  'File.Code.Elixir':      ICONIFY('simple-icons:elixir',     '#4b275f'),
  'File.Code.Erlang':      ICONIFY('simple-icons:erlang',     '#a90533'),
  'File.Code.Clojure':     ICONIFY('simple-icons:clojure',    '#5881d8'),
  'File.Code.Haskell':     ICONIFY('simple-icons:haskell',    '#5d4f85'),
  'File.Code.Zig':         ICONIFY('simple-icons:zig',        '#f7a41d'),
  'File.Code.Nim':         ICONIFY('simple-icons:nim',        '#ffe953'),
  'File.Code.HTML':        PHOSPHOR('FileHtml',  '#e34f26'),
  'File.Code.Vue':         PHOSPHOR('FileVue',   '#42b883'),
  'File.Code.Svelte':      ICONIFY('simple-icons:svelte',     '#ff3e00'),
  'File.Code.Astro':       ICONIFY('simple-icons:astro',      '#ff5d01'),
  'File.Code.CSS':         PHOSPHOR('FileCss',   '#1572b6'),
  'File.Code.SCSS':        PHOSPHOR('FileCss',   '#cc6699'),
  'File.Code.SASS':        PHOSPHOR('FileCss',   '#cc6699'),
  'File.Code.LESS':        PHOSPHOR('FileCss',   '#1d365d'),
  'File.Code.Stylus':      ICONIFY('simple-icons:stylus',     '#333333'),
  'File.Code.Shell':       ICONIFY('mdi:bash',                '#4eaa25'),
  'File.Code.PowerShell':  ICONIFY('simple-icons:powershell', '#5391fe'),
  'File.Code.Batch':       ICONIFY('mdi:console',             '#475569'),
  'File.Code.Terraform':   ICONIFY('simple-icons:terraform',  '#7b42bc'),
  'File.Code.Dockerfile':  ICONIFY('simple-icons:docker',     '#2496ed'),
  'File.Code.Makefile':    ICONIFY('mdi:wrench-outline',      '#64748b'),
  'File.Code.CMake':       ICONIFY('simple-icons:cmake',      '#064f8c'),
  'File.Code.Gradle':      ICONIFY('simple-icons:gradle',     '#02303a'),
  'File.Code.Solidity':    ICONIFY('simple-icons:solidity',   '#363636'),
  'File.Code.GraphQL':     ICONIFY('simple-icons:graphql',    '#e10098'),
  'File.Code.Protobuf':    ICONIFY('simple-icons:protobuf',   '#4285f4'),
  'File.Code.Diff':        PHOSPHOR('FileCode',  '#059669'),

  // ============ Presentations ============
  'File.Presentation':               PHOSPHOR('FilePpt',  '#b7472a'),
  'File.Presentation.PowerPoint':    PHOSPHOR('FilePpt',  '#b7472a'),
  'File.Presentation.Keynote':       PHOSPHOR('FilePpt',  '#0ea5e9'),
  'File.Presentation.OpenDocument':  PHOSPHOR('FilePpt',  '#f97316'),

  // ============ Archives ============
  'File.Archive':          PHOSPHOR('FileArchive', '#d97706'),
  'File.Archive.ZIP':      PHOSPHOR('FileZip',     '#d97706'),
  'File.Archive.RAR':      PHOSPHOR('FileArchive', '#7c3aed'),
  'File.Archive.SevenZip': PHOSPHOR('FileArchive', '#0ea5e9'),
  'File.Archive.TAR':      PHOSPHOR('FileArchive', '#78716c'),
  'File.Archive.GZip':     PHOSPHOR('FileArchive', '#d97706'),
  'File.Archive.BZip2':    PHOSPHOR('FileArchive', '#d97706'),
  'File.Archive.XZ':       PHOSPHOR('FileArchive', '#d97706'),
  'File.Archive.Zstd':     PHOSPHOR('FileArchive', '#059669'),
  'File.Archive.JAR':      ICONIFY('simple-icons:openjdk', '#ed8b00'),

  // ============ Audio ============
  'File.Audio':       PHOSPHOR('FileAudio',  '#a855f7'),
  'File.Audio.MP3':   PHOSPHOR('FileAudio',  '#a855f7'),
  'File.Audio.WAV':   PHOSPHOR('FileAudio',  '#8b5cf6'),
  'File.Audio.FLAC':  PHOSPHOR('FileAudio',  '#7c3aed'),
  'File.Audio.AAC':   PHOSPHOR('FileAudio',  '#a855f7'),
  'File.Audio.OGG':   PHOSPHOR('FileAudio',  '#d946ef'),
  'File.Audio.Opus':  PHOSPHOR('FileAudio',  '#d946ef'),
  'File.Audio.M4A':   PHOSPHOR('FileAudio',  '#a855f7'),
  'File.Audio.WMA':   PHOSPHOR('FileAudio',  '#0ea5e9'),
  'File.Audio.AIFF':  PHOSPHOR('FileAudio',  '#a855f7'),
  'File.Audio.MIDI':  ICONIFY('mdi:piano',   '#e11d48'),

  // ============ Video ============
  'File.Video':            PHOSPHOR('FileVideo', '#ef4444'),
  'File.Video.MP4':        PHOSPHOR('FileVideo', '#ef4444'),
  'File.Video.QuickTime':  PHOSPHOR('FileVideo', '#0ea5e9'),
  'File.Video.WebM':       PHOSPHOR('FileVideo', '#10b981'),
  'File.Video.MKV':        PHOSPHOR('FileVideo', '#7c3aed'),
  'File.Video.AVI':        PHOSPHOR('FileVideo', '#ef4444'),
  'File.Video.WMV':        PHOSPHOR('FileVideo', '#0ea5e9'),
  'File.Video.FLV':        PHOSPHOR('FileVideo', '#ef4444'),
  'File.Video.3GP':        PHOSPHOR('FileVideo', '#64748b'),
  'File.Video.MPEG':       PHOSPHOR('FileVideo', '#dc2626'),
  'File.Video.OGV':        PHOSPHOR('FileVideo', '#d946ef'),

  // ============ Subtitles ============
  'File.Subtitle.SRT': PHOSPHOR('ClosedCaptioning', '#64748b'),
  'File.Subtitle.VTT': PHOSPHOR('ClosedCaptioning', '#64748b'),
  'File.Subtitle.ASS': PHOSPHOR('ClosedCaptioning', '#64748b'),
  'File.Subtitle.SUB': PHOSPHOR('ClosedCaptioning', '#64748b'),

  // ============ eBooks ============
  'File.Ebook.EPUB':   ICONIFY('mdi:book-open-page-variant', '#b45309'),
  'File.Ebook.Mobi':   ICONIFY('mdi:book-open-page-variant', '#d97706'),
  'File.Ebook.Kindle': ICONIFY('simple-icons:amazon',        '#ff9900'),
  'File.Ebook.FB2':    ICONIFY('mdi:book-open-page-variant', '#b45309'),

  // ============ Email / Contacts ============
  'File.Email.EML':      PHOSPHOR('EnvelopeSimple', '#ec4899'),
  'File.Email.Outlook':  ICONIFY('simple-icons:maildotru', '#0078d4'),
  'File.Email.Mbox':     PHOSPHOR('Envelope',       '#be185d'),
  'File.Contact.VCard':  PHOSPHOR('AddressBook',    '#14b8a6'),

  // ============ Calendar ============
  'File.Calendar.ICS': PHOSPHOR('CalendarBlank', '#14b8a6'),

  // ============ Fonts ============
  'File.Font':          ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.Generic':  ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.TrueType': ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.OpenType': ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.WOFF':     ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.WOFF2':    ICONIFY('mdi:format-font', '#f43f5e'),
  'File.Font.EOT':      ICONIFY('mdi:format-font', '#f43f5e'),

  // ============ Config ============
  'File.Config':              PHOSPHOR('Gear',     '#78716c'),
  'File.Config.INI':          PHOSPHOR('FileIni',  '#78716c'),
  'File.Config.Generic':      PHOSPHOR('Gear',     '#78716c'),
  'File.Config.Environment':  ICONIFY('simple-icons:dotenv',       '#eab308'),
  'File.Config.TOML':         ICONIFY('simple-icons:toml',         '#9c4221'),
  'File.Config.Properties':   PHOSPHOR('FileCode', '#78716c'),
  'File.Config.EditorConfig': ICONIFY('simple-icons:editorconfig', '#fefefe'),
  'File.Config.GitIgnore':    ICONIFY('simple-icons:git',          '#f05032'),
  'File.Config.Lock':         PHOSPHOR('Lock',     '#78716c'),

  // ============ Design ============
  'File.Design.Photoshop':         ICONIFY('simple-icons:adobephotoshop',         '#31a8ff'),
  'File.Design.Illustrator':       ICONIFY('simple-icons:adobeillustrator',       '#ff9a00'),
  'File.Design.InDesign':          ICONIFY('simple-icons:adobeindesign',          '#ff3366'),
  'File.Design.Sketch':            ICONIFY('simple-icons:sketch',                 '#f7b500'),
  'File.Design.Figma':             ICONIFY('simple-icons:figma',                  '#f24e1e'),
  'File.Design.XD':                ICONIFY('simple-icons:adobexd',                '#ff26be'),
  'File.Design.AffinityDesigner':  ICONIFY('simple-icons:affinitydesigner',       '#1b72be'),
  'File.Design.AffinityPhoto':     ICONIFY('simple-icons:affinityphoto',          '#7e4dd2'),
  'File.Design.AffinityPublisher': ICONIFY('simple-icons:affinitypublisher',      '#891b47'),
  'File.Design.CorelDraw':         ICONIFY('simple-icons:coreldraw',              '#8fbd2e'),

  // ============ Creative projects ============
  'File.Project.Premiere':      ICONIFY('simple-icons:adobepremierepro',   '#9999ff'),
  'File.Project.AfterEffects':  ICONIFY('simple-icons:adobeaftereffects',  '#9999ff'),
  'File.Project.Ableton':       ICONIFY('simple-icons:abletonlive',        '#000000'),
  'File.Project.FLStudio':      ICONIFY('mdi:piano',                       '#ff7f00'),
  'File.Project.Logic':         ICONIFY('simple-icons:applemusic',         '#f472b6'),
  'File.Project.GarageBand':    ICONIFY('simple-icons:apple',              '#fa5b3a'),
  'File.Project.DaVinci':       ICONIFY('simple-icons:davinciresolve',     '#233a51'),

  // ============ 3D ============
  'File.3D':           ICONIFY('mdi:cube-outline',      '#06b6d4'),
  'File.3D.Generic':   ICONIFY('mdi:cube-outline',      '#06b6d4'),
  'File.3D.Wavefront': ICONIFY('mdi:cube-outline',      '#06b6d4'),
  'File.3D.Blender':   ICONIFY('simple-icons:blender',  '#f5792a'),
  'File.3D.FBX':       ICONIFY('simple-icons:autodesk', '#0696d7'),
  'File.3D.Collada':   ICONIFY('mdi:cube-outline',      '#06b6d4'),
  'File.3D.STL':       ICONIFY('mdi:printer-3d',        '#06b6d4'),
  'File.3D.GLTF':      ICONIFY('mdi:cube-scan',         '#87b40a'),
  'File.3D.3DSMax':    ICONIFY('simple-icons:autodesk', '#0696d7'),
  'File.3D.Cinema4D':  ICONIFY('simple-icons:maxon',    '#011a6a'),

  // ============ CAD ============
  'File.CAD.AutoCAD':  ICONIFY('simple-icons:autodesk',    '#0696d7'),
  'File.CAD.SketchUp': ICONIFY('simple-icons:sketchup',    '#005f9e'),
  'File.CAD.STEP':     ICONIFY('mdi:drawing-box',          '#06b6d4'),
  'File.CAD.IGES':     ICONIFY('mdi:drawing-box',          '#06b6d4'),

  // ============ Executables & disk images ============
  'File.Executable':         ICONIFY('mdi:application',          '#475569'),
  'File.Executable.Windows': ICONIFY('simple-icons:windows11',   '#0078d4'),
  'File.Executable.macOS':   ICONIFY('simple-icons:apple',       '#000000'),
  'File.Executable.Debian':  ICONIFY('simple-icons:debian',      '#a81d33'),
  'File.Executable.RedHat':  ICONIFY('simple-icons:redhat',      '#ee0000'),
  'File.Executable.AppImage':ICONIFY('simple-icons:appimage',    '#1b6ac6'),
  'File.Executable.Android': ICONIFY('simple-icons:android',     '#3ddc84'),
  'File.Executable.iOS':     ICONIFY('simple-icons:ios',         '#000000'),
  'File.DiskImage.DMG':      ICONIFY('mdi:harddisk',             '#64748b'),
  'File.DiskImage.ISO':      ICONIFY('mdi:disc',                 '#64748b'),
  'File.DiskImage.IMG':      ICONIFY('mdi:harddisk',             '#64748b'),

  // ============ Security / Keys ============
  'File.Security':             PHOSPHOR('Shield', '#0f766e'),
  'File.Security.Certificate': PHOSPHOR('Certificate', '#0d9488'),
  'File.Security.Key':         PHOSPHOR('Key',      '#d97706'),
  'File.Security.Keystore':    PHOSPHOR('Vault',    '#0d9488'),
  'File.Security.Encrypted':   PHOSPHOR('LockKey',  '#475569'),

  // ============ Network ============
  'File.Network.Torrent': ICONIFY('mdi:download-network', '#1a9993'),
};

/**
 * Extension → icon lookup. Used by the LocalFile code path (which only has
 * the raw extension, not the full entity type), and for quick lookups where
 * callers have a filename but no pre-computed entity type.
 *
 * Extensions are normalized (lowercase, leading dot optional).
 */
export const FILE_EXTENSION_ICONS: Record<string, FileIconEntry> = {
  // Documents
  'pdf': FILE_TYPE_ICONS['File.Document.PDF'],
  'doc': FILE_TYPE_ICONS['File.Document.Word'],
  'docx': FILE_TYPE_ICONS['File.Document.Word'],
  'odt': FILE_TYPE_ICONS['File.Document.OpenDocument'],
  'rtf': FILE_TYPE_ICONS['File.Document.RichText'],
  'pages': FILE_TYPE_ICONS['File.Document.Pages'],
  'txt': FILE_TYPE_ICONS['File.Document.Text'],
  'text': FILE_TYPE_ICONS['File.Document.Text'],
  'log': FILE_TYPE_ICONS['File.Document.Log'],
  'md': FILE_TYPE_ICONS['File.Document.Markdown'],
  'markdown': FILE_TYPE_ICONS['File.Document.Markdown'],
  'mdx': FILE_TYPE_ICONS['File.Document.Markdown'],
  'rst': FILE_TYPE_ICONS['File.Document.RST'],
  'tex': FILE_TYPE_ICONS['File.Document.LaTeX'],
  'bib': FILE_TYPE_ICONS['File.Document.BibTeX'],

  // Images
  'jpg': FILE_TYPE_ICONS['File.Image.JPEG'],
  'jpeg': FILE_TYPE_ICONS['File.Image.JPEG'],
  'png': FILE_TYPE_ICONS['File.Image.PNG'],
  'gif': FILE_TYPE_ICONS['File.Image.GIF'],
  'svg': FILE_TYPE_ICONS['File.Image.SVG'],
  'webp': FILE_TYPE_ICONS['File.Image.WebP'],
  'avif': FILE_TYPE_ICONS['File.Image.AVIF'],
  'heic': FILE_TYPE_ICONS['File.Image.HEIC'],
  'heif': FILE_TYPE_ICONS['File.Image.HEIC'],
  'bmp': FILE_TYPE_ICONS['File.Image.BMP'],
  'tiff': FILE_TYPE_ICONS['File.Image.TIFF'],
  'tif': FILE_TYPE_ICONS['File.Image.TIFF'],
  'ico': FILE_TYPE_ICONS['File.Image.Icon'],
  'raw': FILE_TYPE_ICONS['File.Image.RAW'],
  'cr2': FILE_TYPE_ICONS['File.Image.RAW'],
  'nef': FILE_TYPE_ICONS['File.Image.RAW'],
  'arw': FILE_TYPE_ICONS['File.Image.RAW'],
  'dng': FILE_TYPE_ICONS['File.Image.RAW'],

  // Data
  'csv': FILE_TYPE_ICONS['File.Data.CSV'],
  'tsv': FILE_TYPE_ICONS['File.Data.TSV'],
  'json': FILE_TYPE_ICONS['File.Data.JSON'],
  'jsonl': FILE_TYPE_ICONS['File.Data.JSONL'],
  'ndjson': FILE_TYPE_ICONS['File.Data.JSONL'],
  'xml': FILE_TYPE_ICONS['File.Data.XML'],
  'yaml': FILE_TYPE_ICONS['File.Data.YAML'],
  'yml': FILE_TYPE_ICONS['File.Data.YAML'],
  'sql': FILE_TYPE_ICONS['File.Data.SQL'],
  'db': FILE_TYPE_ICONS['File.Data.Database'],
  'sqlite': FILE_TYPE_ICONS['File.Data.SQLite'],
  'sqlite3': FILE_TYPE_ICONS['File.Data.SQLite'],
  'parquet': FILE_TYPE_ICONS['File.Data.Parquet'],
  'avro': FILE_TYPE_ICONS['File.Data.Avro'],
  'geojson': FILE_TYPE_ICONS['File.Data.GeoJSON'],
  'kml': FILE_TYPE_ICONS['File.Data.KML'],
  'shp': FILE_TYPE_ICONS['File.Data.Shapefile'],
  'gpx': FILE_TYPE_ICONS['File.Data.GPX'],

  // Spreadsheets
  'xlsx': FILE_TYPE_ICONS['File.Spreadsheet.Excel'],
  'xls': FILE_TYPE_ICONS['File.Spreadsheet.Excel'],
  'xlsm': FILE_TYPE_ICONS['File.Spreadsheet.Excel'],
  'ods': FILE_TYPE_ICONS['File.Spreadsheet.OpenDocument'],
  'numbers': FILE_TYPE_ICONS['File.Spreadsheet.Numbers'],

  // Code
  'js': FILE_TYPE_ICONS['File.Code.JavaScript'],
  'mjs': FILE_TYPE_ICONS['File.Code.JavaScript'],
  'cjs': FILE_TYPE_ICONS['File.Code.JavaScript'],
  'jsx': FILE_TYPE_ICONS['File.Code.JavaScript'],
  'ts': FILE_TYPE_ICONS['File.Code.TypeScript'],
  'tsx': FILE_TYPE_ICONS['File.Code.TypeScript'],
  'mts': FILE_TYPE_ICONS['File.Code.TypeScript'],
  'py': FILE_TYPE_ICONS['File.Code.Python'],
  'pyw': FILE_TYPE_ICONS['File.Code.Python'],
  'ipynb': FILE_TYPE_ICONS['File.Code.Jupyter'],
  'java': FILE_TYPE_ICONS['File.Code.Java'],
  'kt': FILE_TYPE_ICONS['File.Code.Kotlin'],
  'kts': FILE_TYPE_ICONS['File.Code.Kotlin'],
  'scala': FILE_TYPE_ICONS['File.Code.Scala'],
  'groovy': FILE_TYPE_ICONS['File.Code.Groovy'],
  'cpp': FILE_TYPE_ICONS['File.Code.CPlusPlus'],
  'cxx': FILE_TYPE_ICONS['File.Code.CPlusPlus'],
  'cc': FILE_TYPE_ICONS['File.Code.CPlusPlus'],
  'c': FILE_TYPE_ICONS['File.Code.C'],
  'h': FILE_TYPE_ICONS['File.Code.Header'],
  'hpp': FILE_TYPE_ICONS['File.Code.Header'],
  'hh': FILE_TYPE_ICONS['File.Code.Header'],
  'cs': FILE_TYPE_ICONS['File.Code.CSharp'],
  'fs': FILE_TYPE_ICONS['File.Code.FSharp'],
  'vb': FILE_TYPE_ICONS['File.Code.VisualBasic'],
  'php': FILE_TYPE_ICONS['File.Code.PHP'],
  'rb': FILE_TYPE_ICONS['File.Code.Ruby'],
  'go': FILE_TYPE_ICONS['File.Code.Go'],
  'rs': FILE_TYPE_ICONS['File.Code.Rust'],
  'swift': FILE_TYPE_ICONS['File.Code.Swift'],
  'r': FILE_TYPE_ICONS['File.Code.R'],
  'dart': FILE_TYPE_ICONS['File.Code.Dart'],
  'lua': FILE_TYPE_ICONS['File.Code.Lua'],
  'pl': FILE_TYPE_ICONS['File.Code.Perl'],
  'pm': FILE_TYPE_ICONS['File.Code.Perl'],
  'elm': FILE_TYPE_ICONS['File.Code.Elm'],
  'ex': FILE_TYPE_ICONS['File.Code.Elixir'],
  'exs': FILE_TYPE_ICONS['File.Code.Elixir'],
  'erl': FILE_TYPE_ICONS['File.Code.Erlang'],
  'clj': FILE_TYPE_ICONS['File.Code.Clojure'],
  'hs': FILE_TYPE_ICONS['File.Code.Haskell'],
  'zig': FILE_TYPE_ICONS['File.Code.Zig'],
  'nim': FILE_TYPE_ICONS['File.Code.Nim'],
  'html': FILE_TYPE_ICONS['File.Code.HTML'],
  'htm': FILE_TYPE_ICONS['File.Code.HTML'],
  'xhtml': FILE_TYPE_ICONS['File.Code.HTML'],
  'vue': FILE_TYPE_ICONS['File.Code.Vue'],
  'svelte': FILE_TYPE_ICONS['File.Code.Svelte'],
  'astro': FILE_TYPE_ICONS['File.Code.Astro'],
  'css': FILE_TYPE_ICONS['File.Code.CSS'],
  'scss': FILE_TYPE_ICONS['File.Code.SCSS'],
  'sass': FILE_TYPE_ICONS['File.Code.SASS'],
  'less': FILE_TYPE_ICONS['File.Code.LESS'],
  'styl': FILE_TYPE_ICONS['File.Code.Stylus'],
  'sh': FILE_TYPE_ICONS['File.Code.Shell'],
  'bash': FILE_TYPE_ICONS['File.Code.Shell'],
  'zsh': FILE_TYPE_ICONS['File.Code.Shell'],
  'fish': FILE_TYPE_ICONS['File.Code.Shell'],
  'ps1': FILE_TYPE_ICONS['File.Code.PowerShell'],
  'psm1': FILE_TYPE_ICONS['File.Code.PowerShell'],
  'bat': FILE_TYPE_ICONS['File.Code.Batch'],
  'cmd': FILE_TYPE_ICONS['File.Code.Batch'],
  'tf': FILE_TYPE_ICONS['File.Code.Terraform'],
  'tfvars': FILE_TYPE_ICONS['File.Code.Terraform'],
  'dockerfile': FILE_TYPE_ICONS['File.Code.Dockerfile'],
  'makefile': FILE_TYPE_ICONS['File.Code.Makefile'],
  'cmake': FILE_TYPE_ICONS['File.Code.CMake'],
  'gradle': FILE_TYPE_ICONS['File.Code.Gradle'],
  'sol': FILE_TYPE_ICONS['File.Code.Solidity'],
  'graphql': FILE_TYPE_ICONS['File.Code.GraphQL'],
  'gql': FILE_TYPE_ICONS['File.Code.GraphQL'],
  'proto': FILE_TYPE_ICONS['File.Code.Protobuf'],
  'diff': FILE_TYPE_ICONS['File.Code.Diff'],
  'patch': FILE_TYPE_ICONS['File.Code.Diff'],

  // Presentations
  'ppt': FILE_TYPE_ICONS['File.Presentation.PowerPoint'],
  'pptx': FILE_TYPE_ICONS['File.Presentation.PowerPoint'],
  'pptm': FILE_TYPE_ICONS['File.Presentation.PowerPoint'],
  'odp': FILE_TYPE_ICONS['File.Presentation.OpenDocument'],
  'key': FILE_TYPE_ICONS['File.Security.Key'],

  // Archives
  'zip': FILE_TYPE_ICONS['File.Archive.ZIP'],
  'rar': FILE_TYPE_ICONS['File.Archive.RAR'],
  '7z': FILE_TYPE_ICONS['File.Archive.SevenZip'],
  'tar': FILE_TYPE_ICONS['File.Archive.TAR'],
  'gz': FILE_TYPE_ICONS['File.Archive.GZip'],
  'tgz': FILE_TYPE_ICONS['File.Archive.GZip'],
  'bz2': FILE_TYPE_ICONS['File.Archive.BZip2'],
  'xz': FILE_TYPE_ICONS['File.Archive.XZ'],
  'zst': FILE_TYPE_ICONS['File.Archive.Zstd'],
  'jar': FILE_TYPE_ICONS['File.Archive.JAR'],
  'war': FILE_TYPE_ICONS['File.Archive.JAR'],

  // Audio
  'mp3': FILE_TYPE_ICONS['File.Audio.MP3'],
  'wav': FILE_TYPE_ICONS['File.Audio.WAV'],
  'flac': FILE_TYPE_ICONS['File.Audio.FLAC'],
  'aac': FILE_TYPE_ICONS['File.Audio.AAC'],
  'ogg': FILE_TYPE_ICONS['File.Audio.OGG'],
  'oga': FILE_TYPE_ICONS['File.Audio.OGG'],
  'opus': FILE_TYPE_ICONS['File.Audio.Opus'],
  'm4a': FILE_TYPE_ICONS['File.Audio.M4A'],
  'wma': FILE_TYPE_ICONS['File.Audio.WMA'],
  'aiff': FILE_TYPE_ICONS['File.Audio.AIFF'],
  'aif': FILE_TYPE_ICONS['File.Audio.AIFF'],
  'mid': FILE_TYPE_ICONS['File.Audio.MIDI'],
  'midi': FILE_TYPE_ICONS['File.Audio.MIDI'],

  // Video
  'mp4': FILE_TYPE_ICONS['File.Video.MP4'],
  'm4v': FILE_TYPE_ICONS['File.Video.MP4'],
  'avi': FILE_TYPE_ICONS['File.Video.AVI'],
  'mov': FILE_TYPE_ICONS['File.Video.QuickTime'],
  'qt': FILE_TYPE_ICONS['File.Video.QuickTime'],
  'wmv': FILE_TYPE_ICONS['File.Video.WMV'],
  'flv': FILE_TYPE_ICONS['File.Video.FLV'],
  'webm': FILE_TYPE_ICONS['File.Video.WebM'],
  'mkv': FILE_TYPE_ICONS['File.Video.MKV'],
  '3gp': FILE_TYPE_ICONS['File.Video.3GP'],
  'mpg': FILE_TYPE_ICONS['File.Video.MPEG'],
  'mpeg': FILE_TYPE_ICONS['File.Video.MPEG'],
  'ogv': FILE_TYPE_ICONS['File.Video.OGV'],

  // Subtitles
  'srt': FILE_TYPE_ICONS['File.Subtitle.SRT'],
  'vtt': FILE_TYPE_ICONS['File.Subtitle.VTT'],
  'ass': FILE_TYPE_ICONS['File.Subtitle.ASS'],
  'sub': FILE_TYPE_ICONS['File.Subtitle.SUB'],

  // eBooks
  'epub': FILE_TYPE_ICONS['File.Ebook.EPUB'],
  'mobi': FILE_TYPE_ICONS['File.Ebook.Mobi'],
  'azw': FILE_TYPE_ICONS['File.Ebook.Kindle'],
  'azw3': FILE_TYPE_ICONS['File.Ebook.Kindle'],
  'fb2': FILE_TYPE_ICONS['File.Ebook.FB2'],

  // Email / PIM
  'eml': FILE_TYPE_ICONS['File.Email.EML'],
  'msg': FILE_TYPE_ICONS['File.Email.Outlook'],
  'mbox': FILE_TYPE_ICONS['File.Email.Mbox'],
  'vcf': FILE_TYPE_ICONS['File.Contact.VCard'],
  'vcard': FILE_TYPE_ICONS['File.Contact.VCard'],

  // Calendar
  'ics': FILE_TYPE_ICONS['File.Calendar.ICS'],
  'ical': FILE_TYPE_ICONS['File.Calendar.ICS'],
  'ifb': FILE_TYPE_ICONS['File.Calendar.ICS'],

  // Fonts
  'ttf': FILE_TYPE_ICONS['File.Font.TrueType'],
  'otf': FILE_TYPE_ICONS['File.Font.OpenType'],
  'woff': FILE_TYPE_ICONS['File.Font.WOFF'],
  'woff2': FILE_TYPE_ICONS['File.Font.WOFF2'],
  'eot': FILE_TYPE_ICONS['File.Font.EOT'],

  // Config
  'ini': FILE_TYPE_ICONS['File.Config.INI'],
  'cfg': FILE_TYPE_ICONS['File.Config.INI'],
  'conf': FILE_TYPE_ICONS['File.Config.Generic'],
  'config': FILE_TYPE_ICONS['File.Config.Generic'],
  'env': FILE_TYPE_ICONS['File.Config.Environment'],
  'toml': FILE_TYPE_ICONS['File.Config.TOML'],
  'properties': FILE_TYPE_ICONS['File.Config.Properties'],
  'editorconfig': FILE_TYPE_ICONS['File.Config.EditorConfig'],
  'gitignore': FILE_TYPE_ICONS['File.Config.GitIgnore'],
  'gitattributes': FILE_TYPE_ICONS['File.Config.GitIgnore'],
  'lock': FILE_TYPE_ICONS['File.Config.Lock'],

  // Design
  'psd': FILE_TYPE_ICONS['File.Design.Photoshop'],
  'psb': FILE_TYPE_ICONS['File.Design.Photoshop'],
  'ai': FILE_TYPE_ICONS['File.Design.Illustrator'],
  'indd': FILE_TYPE_ICONS['File.Design.InDesign'],
  'idml': FILE_TYPE_ICONS['File.Design.InDesign'],
  'sketch': FILE_TYPE_ICONS['File.Design.Sketch'],
  'fig': FILE_TYPE_ICONS['File.Design.Figma'],
  'xd': FILE_TYPE_ICONS['File.Design.XD'],
  'afdesign': FILE_TYPE_ICONS['File.Design.AffinityDesigner'],
  'afphoto': FILE_TYPE_ICONS['File.Design.AffinityPhoto'],
  'afpub': FILE_TYPE_ICONS['File.Design.AffinityPublisher'],
  'cdr': FILE_TYPE_ICONS['File.Design.CorelDraw'],

  // Creative projects
  'prproj': FILE_TYPE_ICONS['File.Project.Premiere'],
  'aep': FILE_TYPE_ICONS['File.Project.AfterEffects'],
  'als': FILE_TYPE_ICONS['File.Project.Ableton'],
  'flp': FILE_TYPE_ICONS['File.Project.FLStudio'],
  'logicx': FILE_TYPE_ICONS['File.Project.Logic'],
  'band': FILE_TYPE_ICONS['File.Project.GarageBand'],
  'drp': FILE_TYPE_ICONS['File.Project.DaVinci'],

  // 3D
  'obj': FILE_TYPE_ICONS['File.3D.Wavefront'],
  'blend': FILE_TYPE_ICONS['File.3D.Blender'],
  'fbx': FILE_TYPE_ICONS['File.3D.FBX'],
  'dae': FILE_TYPE_ICONS['File.3D.Collada'],
  'stl': FILE_TYPE_ICONS['File.3D.STL'],
  'gltf': FILE_TYPE_ICONS['File.3D.GLTF'],
  'glb': FILE_TYPE_ICONS['File.3D.GLTF'],
  '3ds': FILE_TYPE_ICONS['File.3D.3DSMax'],
  'max': FILE_TYPE_ICONS['File.3D.3DSMax'],
  'c4d': FILE_TYPE_ICONS['File.3D.Cinema4D'],

  // CAD
  'dwg': FILE_TYPE_ICONS['File.CAD.AutoCAD'],
  'dxf': FILE_TYPE_ICONS['File.CAD.AutoCAD'],
  'skp': FILE_TYPE_ICONS['File.CAD.SketchUp'],
  'step': FILE_TYPE_ICONS['File.CAD.STEP'],
  'stp': FILE_TYPE_ICONS['File.CAD.STEP'],
  'iges': FILE_TYPE_ICONS['File.CAD.IGES'],
  'igs': FILE_TYPE_ICONS['File.CAD.IGES'],

  // Executables & disk images
  'exe': FILE_TYPE_ICONS['File.Executable.Windows'],
  'msi': FILE_TYPE_ICONS['File.Executable.Windows'],
  'app': FILE_TYPE_ICONS['File.Executable.macOS'],
  'pkg': FILE_TYPE_ICONS['File.Executable.macOS'],
  'deb': FILE_TYPE_ICONS['File.Executable.Debian'],
  'rpm': FILE_TYPE_ICONS['File.Executable.RedHat'],
  'appimage': FILE_TYPE_ICONS['File.Executable.AppImage'],
  'apk': FILE_TYPE_ICONS['File.Executable.Android'],
  'ipa': FILE_TYPE_ICONS['File.Executable.iOS'],
  'dmg': FILE_TYPE_ICONS['File.DiskImage.DMG'],
  'iso': FILE_TYPE_ICONS['File.DiskImage.ISO'],
  'img': FILE_TYPE_ICONS['File.DiskImage.IMG'],

  // Security
  'pem': FILE_TYPE_ICONS['File.Security.Certificate'],
  'crt': FILE_TYPE_ICONS['File.Security.Certificate'],
  'cer': FILE_TYPE_ICONS['File.Security.Certificate'],
  'p12': FILE_TYPE_ICONS['File.Security.Keystore'],
  'pfx': FILE_TYPE_ICONS['File.Security.Keystore'],
  'gpg': FILE_TYPE_ICONS['File.Security.Encrypted'],
  'asc': FILE_TYPE_ICONS['File.Security.Encrypted'],

  // Network
  'torrent': FILE_TYPE_ICONS['File.Network.Torrent'],
};

/**
 * Generic folder icon used by the LocalFile resolver for directory entries.
 */
export const FOLDER_ICON: FileIconEntry = PHOSPHOR('Folder', '#f59e0b', 'fill');

/**
 * Ultimate fallback icon when nothing else matches.
 */
export const GENERIC_FILE_ICON: FileIconEntry = PHOSPHOR('File', '#64748b');

/**
 * Resolve an icon for a file entity type. Walks the hierarchy from most
 * specific to least specific and returns null if no ancestor is mapped.
 */
export function getIconForEntityType(entityType: string): FileIconEntry | null {
  if (!entityType) return null;
  if (FILE_TYPE_ICONS[entityType]) return FILE_TYPE_ICONS[entityType];

  const parts = entityType.split('.');
  while (parts.length > 1) {
    parts.pop();
    const candidate = parts.join('.');
    if (FILE_TYPE_ICONS[candidate]) return FILE_TYPE_ICONS[candidate];
  }
  return null;
}

/**
 * Resolve an icon from a filename. Handles extensions with or without a
 * leading dot; empty-extension filenames (Dockerfile, Makefile, etc.) fall
 * back to the full lowercased filename lookup.
 */
export function getIconForFilename(filename: string): FileIconEntry | null {
  if (!filename) return null;
  const name = filename.toLowerCase();

  if (FILE_EXTENSION_ICONS[name]) return FILE_EXTENSION_ICONS[name];

  const lastDot = name.lastIndexOf('.');
  if (lastDot < 0 || lastDot === name.length - 1) return null;
  const ext = name.slice(lastDot + 1);
  return FILE_EXTENSION_ICONS[ext] ?? null;
}
